import React, { useEffect, useRef } from 'react';
import { Toast as ToastType } from './ToastContext';


interface ToastProps extends ToastType {
  onRemove: (id: string) => void;
}

const icons = {
  success: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  ),
  warning: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
    </svg>
  ),
};

const titles = {
  success: 'Success!',
  error: 'Error!',
  warning: 'Warning!',
  info: 'Information',
};

export const Toast: React.FC<ToastProps> = ({ id, message, type = 'info', position = 'top-right', onRemove }) => {
  const toastRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (progressRef.current) {
        progressRef.current.style.animation = 'none';
      }
      if (toastRef.current) {
        toastRef.current.classList.remove('toast-enter');
        toastRef.current.classList.add('toast-exit');
      }
      setTimeout(() => onRemove(id), 300);
    }, 3500);

    return () => clearTimeout(timer);
  }, [id, onRemove]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (progressRef.current) {
      progressRef.current.style.animation = 'none';
    }
    if (toastRef.current) {
      toastRef.current.classList.remove('toast-enter');
      toastRef.current.classList.add('toast-exit');
    }
    setTimeout(() => onRemove(id), 300);
  };

  return (
    <div
      ref={toastRef}
      className={`toast toast-${type} toast-enter`}
      id={`toast-${id}`}
    >
      <div ref={progressRef} className={`progress-bar progress-${type}`} />
      <div className="toast-content">
        <div className={`icon-circle icon-${type}`}>
          <div className="icon-core">{icons[type]}</div>
        </div>
        <div className="toast-text">
          <h3 className="toast-title">{titles[type]}</h3>
          <p className="toast-message">{message}</p>
        </div>
        <button onClick={handleClose} className="toast-close">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  );
};